// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable i18next/no-literal-string */
import { CONFIG_TRUSTARC_DOMAIN } from '@src/config/env'
import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'
import { USER_CONSENT_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { removeProtocol } from '@src/quince-site-common/src/utils/urlUtils'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'

import { initialiseTrustARCPromise, isTrustARCScriptLoading, TRUSTARC_CONFIG } from './TrustArc'
import { TRUSTE_CONSENT, TRUSTE_CONSENT_DECISION, TTruste } from './trustarc.type'

declare global {
  interface Window {
    truste: TTruste
  }
}

class TrustARC {
  private resolveTrustArcFeatureEnablePromise: (value: boolean) => void = () => null
  private isTrustARCFeatureEnablePromise = new Promise<boolean>(resolve => {
    this.resolveTrustArcFeatureEnablePromise = resolve
  })

  // Check if Truste ARC feature is enabled
  private async isTrustARCFeatureEnable() {
    const isFeatureEnable = await this.isTrustARCFeatureEnablePromise
    return isFeatureEnable
  }

  async init() {
    // Check if Truste ARC feature is enabled
    const isFeatureEnable = await this.isTrustARCFeatureEnable()

    // Check if Truste ARC script is enabled and feature is enabled
    if (!TRUSTARC_CONFIG.config.enabled || !isFeatureEnable) return

    // Initialize Truste ARC script
    executeFireAndForgotTask(async () => {
      if (TRUSTARC_CONFIG.initialize) {
        await TRUSTARC_CONFIG.initialize()
      }
    }, TRUSTARC_CONFIG.config.eventIdealTimeout)
  }

  hasAllowedAdvertisingTrackerConsent() {
    const isCountryCompliant = getIsCountryCompliant()

    // TrustArc CMA not available — fall back to browser cookie
    if (typeof window.truste?.cma === 'undefined') {
      if (isCountryCompliant) {
        return getCookie(USER_CONSENT_COOKIES.ADVERTISING) === 'true'
      }
      return true // US/CA: allow by default
    }

    const { consentDecision } = window.truste.cma.callApi<{ consentDecision: TRUSTE_CONSENT_DECISION[] }>(
      'getGDPRConsentDecision',
      CONFIG_TRUSTARC_DOMAIN,
    )

    if (isCountryCompliant) {
      // UK (opt-in): [0] = no preference → block. Only ADVERTISING=3 means explicit consent.
      return consentDecision.includes(TRUSTE_CONSENT_DECISION.ADVERTISING)
    }

    // US/CA (opt-out): [0] = implied consent granted
    return (
      consentDecision.includes(TRUSTE_CONSENT_DECISION.ADVERTISING) ||
      consentDecision.includes(TRUSTE_CONSENT_DECISION.ALL)
    )
  }

  // Open Truste CCM Popup
  handleOpenTrusteCCMPopup() {
    window.truste.eu.clickListener()
  }

  async checkTrusteCCMConsent(domain: string) {
    const isFeatureEnable = await this.isTrustARCFeatureEnable()
    if (!isFeatureEnable) return true

    // Check if Truste ARC script is loaded
    await this.hasTrusteArcScriptLoaded()

    // Check Truste CCM Consent
    const consent = this.checkTrusteCCMConsentFromAPI(removeProtocol(domain))

    return consent === TRUSTE_CONSENT.APPROVED
  }

  // get Truste ARC feature enable promise
  getReolverTrustArcFeatureEnablePromise() {
    return this.resolveTrustArcFeatureEnablePromise
  }

  // Check if Truste ARC window object is loaded
  private checkTrusteCCMAPILoaded(): Promise<boolean> {
    return new Promise(resolve => {
      if (typeof window.truste?.cma !== 'undefined') {
        resolve(true)
        return
      }
      // Bound the poll (10s) so a CMA API that never initialises — CDN latency, a
      // script error, or an ad-blocker that allows the script but blocks CMA init —
      // can't leave this Promise (and every 3P integration awaiting consent) hanging
      // forever. On timeout resolve(false); callers fall through to a DENIED consent
      // result via checkTrusteCCMConsentFromAPI and simply skip initialisation.
      const timeout = setTimeout(() => {
        clearInterval(interval)
        resolve(false)
      }, 10000)
      const interval = setInterval(() => {
        if (typeof window.truste?.cma !== 'undefined') {
          clearInterval(interval)
          clearTimeout(timeout)
          resolve(true)
        }
      }, 50)
    })
  }

  // Check if Truste ARC script is loaded
  private async hasTrusteArcScriptLoaded() {
    if (isTrustARCScriptLoading) {
      // Wait for Truste ARC script to load
      await initialiseTrustARCPromise
    }
    // Always wait for CMA API — it initialises slightly after script onLoad
    // even when isTrustARCScriptLoading is already false
    await this.checkTrusteCCMAPILoaded()
    return true
  }

  // Check Truste CCM Consent from Truste API
  private checkTrusteCCMConsentFromAPI(domain: string): TRUSTE_CONSENT {
    if (typeof window.truste?.cma === 'undefined') return TRUSTE_CONSENT.DENIED

    const currentDomain = window.location.host

    const res = window.truste.cma.callApi<{ consent: TRUSTE_CONSENT }>(
      'getConsent',
      currentDomain,
      domain,
      currentDomain,
    )

    // If domain is not present in Truste CCM, then return denied if advertising is not allowed
    return typeof res.consent !== 'undefined' ? res.consent : TRUSTE_CONSENT.DENIED
  }
}

const TrustArcCl = new TrustARC()

export default TrustArcCl
